"use client";
import { useEffect, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { AlertTriangle, Loader2, Trash2, Lock } from "lucide-react";
import { dataResetService } from "@/state/business/dataReset.service";

/**
 * Every area an admin can clear, with what it actually removes.
 *
 * `requires` is the set of areas that must go WITH this one — a record cannot
 * outlive the documents that point at it, and this system is configured so
 * that clearing a history also zeroes stock.
 */
const AREAS = [
    {
        key: "sales",
        label: "Sales & returns",
        description: "Receipts, sale lines, customer payments and sale returns",
        requires: ["stock"],
    },
    {
        key: "purchases",
        label: "Purchases & supplier payments",
        description: "Purchase invoices, their lines, payments and returns",
        requires: ["stock"],
    },
    {
        key: "stock",
        label: "Stock on hand",
        description: "Every stock record, transfer and movement — quantities reset to zero",
        requires: [],
    },
    {
        key: "expenses",
        label: "Expenses",
        description: "Recorded operating costs",
        requires: [],
    },
    {
        key: "customers",
        label: "Customers",
        description: "Customer records (their sales must go too)",
        requires: ["sales", "stock"],
    },
    {
        key: "suppliers",
        label: "Suppliers",
        description: "Supplier records (their purchases must go too)",
        requires: ["purchases", "stock"],
    },
    {
        key: "products",
        label: "Products",
        description: "The product catalogue itself",
        requires: ["sales", "purchases", "stock"],
    },
    {
        key: "catalog",
        label: "Categories, brands & units",
        description: "Catalogue lookups",
        requires: ["products", "sales", "purchases", "stock"],
    },
    {
        key: "audit",
        label: "Activity log",
        description: "The record of who did what",
        requires: [],
    },
];

/** Mirrors the server's expandAreas — shown before the button is pressed. */
const expand = (chosen) => {
    const out = new Set(chosen);
    AREAS.forEach(({ key, requires }) => {
        if (out.has(key)) requires.forEach((r) => out.add(r));
    });
    return out;
};

export default function ClearDataPage() {
    const { toast } = useToast();
    const [counts, setCounts] = useState({});
    const [chosen, setChosen] = useState([]);
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);

    const loadCounts = async () => {
        try {
            const res = await dataResetService.counts();
            setCounts(res?.data || {});
        } catch (err) {
            toast({
                variant: "destructive",
                title: "Could not read the data counts",
                description: err?.response?.data?.message || err.message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const effective = expand(chosen);
    const implied = [...effective].filter((a) => !chosen.includes(a));
    const rowsAffected = [...effective].reduce(
        (total, area) => total + (counts[area] || 0),
        0
    );
    const armed = chosen.length > 0 && password.length > 0 && !working;

    const toggle = (key) => {
        setChosen((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const handleClear = async () => {
        setWorking(true);
        try {
            const res = await dataResetService.clear({ areas: chosen, password });
            const removed = res?.data?.removed || {};
            const total = Object.values(removed).reduce((t, n) => t + n, 0);
            toast({
                title: "Data cleared",
                description: `${total.toLocaleString()} record${total === 1 ? "" : "s"} removed.`,
            });
            setChosen([]);
            setPassword("");
            await loadCounts();
        } catch (err) {
            toast({
                variant: "destructive",
                title: "Nothing was cleared",
                description: err?.response?.data?.message || err.message,
            });
        } finally {
            setWorking(false);
        }
    };

    return (
        <div className="p-4 md:p-6 max-w-4xl">
            <div className="mb-5">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    Clear Data
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    Permanently delete business records. There is no undo.
                </p>
            </div>

            {/* The warning leads, and carries the number — "clear sales" should
                be a quantity, not an abstraction. */}
            <div className="flex items-start gap-3 p-4 mb-5 border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700 dark:text-slate-300">
                    <p className="font-semibold text-red-700 dark:text-red-400">
                        {rowsAffected > 0
                            ? `${rowsAffected.toLocaleString()} records will be deleted and cannot be recovered.`
                            : "Deleted records cannot be recovered."}
                    </p>
                    <p className="mt-1">
                        Take a database backup before you continue. Your users, roles,
                        shops and business settings are never touched.
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">What should be cleared?</CardTitle>
                    <CardDescription>
                        Tick only what you want to lose. Anything required by your
                        choice is ticked automatically and shown below.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-1">
                    {loading ? (
                        <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
                            <Loader2 className="h-4 w-4 animate-spin" /> Counting records...
                        </div>
                    ) : (
                        AREAS.map((area) => {
                            const isImplied = implied.includes(area.key);
                            const isOn = chosen.includes(area.key) || isImplied;
                            return (
                                <div
                                    key={area.key}
                                    className={`flex items-start gap-3 p-3 border-b last:border-b-0 border-slate-100 dark:border-slate-800 ${
                                        isImplied ? "" : "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    } transition-colors`}
                                    onClick={() => !isImplied && toggle(area.key)}
                                >
                                    <Checkbox
                                        checked={isOn}
                                        // An implied area cannot be unticked here — untick
                                        // whatever requires it instead.
                                        disabled={isImplied}
                                        onCheckedChange={() => toggle(area.key)}
                                        className="mt-0.5"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                {area.label}
                                            </span>
                                            {isImplied && (
                                                <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                                                    required
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {area.description}
                                        </p>
                                    </div>
                                    <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums whitespace-nowrap">
                                        {(counts[area.key] || 0).toLocaleString()} record
                                        {counts[area.key] === 1 ? "" : "s"}
                                    </span>
                                </div>
                            );
                        })
                    )}

                    {implied.length > 0 && (
                        <div className="mt-3 p-3 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-slate-700 dark:text-slate-300">
                            Also going, because your selection needs it:{" "}
                            <span className="font-semibold">
                                {implied
                                    .map((k) => AREAS.find((a) => a.key === k)?.label)
                                    .join(", ")}
                            </span>
                            .
                        </div>
                    )}

                    <div className="pt-5">
                        <Label htmlFor="confirm-password" className="text-sm">
                            Confirm with your password
                        </Label>
                        <div className="relative mt-1.5 max-w-sm">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                                id="confirm-password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Your password"
                                className="pl-9"
                            />
                        </div>
                        <p className="mt-1.5 text-xs text-slate-500">
                            Checked against your own account — the clear is recorded
                            against your name.
                        </p>
                    </div>

                    <div className="pt-5">
                        <Button
                            type="button"
                            variant="destructive"
                            disabled={!armed}
                            onClick={handleClear}
                            className="cursor-pointer"
                        >
                            {working ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            {chosen.length === 0
                                ? "Select what to clear"
                                : `Clear ${rowsAffected.toLocaleString()} records`}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
